/**
 * Request-history timeline model (#4861, identity framing #4999).
 *
 * Turns the raw proposal rounds + terminal decision into the ordered, human
 * "Requested / Countered / Confirmed / Denied by <who> on <when>" entries the
 * `BreakProposalChain` widget renders. Kept pure (no React, no I/O) so the
 * wording — the part QA reads back verbatim — is unit-testable in isolation.
 *
 * The "who" is framed relative to the viewer's IDENTITY (their Runhuman user
 * id), not their role: an entry whose actor id matches `viewerUserId` reads
 * "you"; a different actor shows their resolved display name; an actor whose
 * identity can't be resolved (no id match and no name) drops the "by <who>"
 * clause entirely — "Confirmed on <when>" rather than guessing a role noun.
 */
import type {
  BreakRequestDecisionSummary,
  BreakRequestProposal,
} from '@runhuman/shared';
import {
  formatBreakDateMediumInTz,
  formatBreakWindowInTz,
  formatLocalTime12,
} from './break-request-format';

// "Apr 4, 2026 at 1:30 PM" — the request-history provenance stamp, using the
// shortened named date (#5094) so it matches the dialog's From / Time lines.
function formatProvenanceStamp(isoInstant: string, timezone: string): string {
  return `${formatBreakDateMediumInTz(isoInstant, timezone)} at ${formatLocalTime12(isoInstant, timezone)}`;
}

export type HistoryTone = 'neutral' | 'confirmed' | 'denied' | 'cancelled';

export interface RequestHistoryEntry {
  readonly key: string;
  /** e.g. "Requested by you on 05/06/26 at 1:00 PM". */
  readonly headline: string;
  /** Proposed window for a round; `null` on the terminal decision entry. */
  readonly window: string | null;
  /** Per-round rationale or the decision reason; `null` when absent. */
  readonly detail: string | null;
  readonly tone: HistoryTone;
}

/**
 * "<Verb> by you" for the viewer's own action, "<Verb> by <name>" for someone
 * else, and the identity-less "<Verb>" when the actor can't be resolved (#4999).
 */
function headline(
  verb: string,
  actorUserId: string | null,
  displayName: string | null,
  viewerUserId: string | null,
  when: string,
): string {
  const who = actorLabel(actorUserId, displayName, viewerUserId);
  return who ? `${verb} by ${who} on ${when}` : `${verb} on ${when}`;
}

function actorLabel(
  actorUserId: string | null,
  displayName: string | null,
  viewerUserId: string | null,
): string | null {
  if (actorUserId && viewerUserId && actorUserId === viewerUserId) return 'you';
  return displayName;
}

const DECISION_VERB: Readonly<Record<BreakRequestDecisionSummary['status'], string>> = {
  approved: 'Confirmed',
  declined: 'Denied',
  cancelled: 'Cancelled',
};

const DECISION_TONE: Readonly<Record<BreakRequestDecisionSummary['status'], HistoryTone>> = {
  approved: 'confirmed',
  declined: 'denied',
  cancelled: 'cancelled',
};

export function buildRequestHistoryEntries(
  proposals: readonly BreakRequestProposal[],
  decision: BreakRequestDecisionSummary | null | undefined,
  viewerUserId: string | null,
  timezone: string,
): readonly RequestHistoryEntry[] {
  const entries: RequestHistoryEntry[] = proposals.map((p) => ({
    key: p.id,
    headline: headline(
      p.roundIndex === 0 ? 'Requested' : 'Countered',
      p.actorUserId,
      p.actorDisplayName,
      viewerUserId,
      formatProvenanceStamp(p.createdAt, timezone),
    ),
    window: `${formatBreakWindowInTz(p.startsAt, p.endsAt, timezone)} on ${formatBreakDateMediumInTz(p.startsAt, timezone)}`,
    detail: p.rationale,
    tone: 'neutral' as const,
  }));

  if (decision) {
    entries.push({
      key: `decision-${decision.decidedAt}`,
      headline: headline(
        DECISION_VERB[decision.status],
        decision.decidedByUserId,
        decision.decidedByDisplayName,
        viewerUserId,
        formatProvenanceStamp(decision.decidedAt, timezone),
      ),
      window: null,
      detail: decision.decisionReason,
      tone: DECISION_TONE[decision.status],
    });
  }

  // Most-recent-first: the terminal decision (if any) sits at the top, then
  // counters in reverse-round order down to the original request (#4861).
  return entries.reverse();
}
